import { getLoyaltyProgram } from '@wix/ambassador-loyalty-v1-program/http';
import { ProgramStatus, LoyaltyProgram } from '@wix/ambassador-loyalty-v1-program/types';
import { getCurrentMemberAccount } from '@wix/ambassador-loyalty-v1-account/http';
import { LoyaltyAccount } from '@wix/ambassador-loyalty-v1-account/types';
import { listRewards } from '@wix/ambassador-loyalty-v1-reward/http';
import { Reward } from '@wix/ambassador-loyalty-v1-reward/types';
import { getCurrentMemberCoupons, queryCouponTemplate } from '@wix/ambassador-loyalty-v1-coupon/http';
import { CouponReference, LoyaltyCoupon } from '@wix/ambassador-loyalty-v1-coupon/types';
import { queryReferralRewards } from '@wix/ambassador-loyalty-referral-v1-referral-reward/http';
import { ReferralReward } from '@wix/ambassador-loyalty-referral-v1-referral-reward/types';
import { ControllerFlowAPI, IHttpClient } from '@wix/yoshi-flow-editor';
import { getTiersProgram } from '@wix/ambassador-loyalty-v1-tier/http';
import { getCouponEntityName } from '@wix/referrals-reward-descriptions';

import { AppInstallStatus } from '../getAppInstallStatus';
import { TiersProgram } from '../../types/domain';

interface LoadDataParams {
  flowAPI: ControllerFlowAPI;
  appInstallStatus: AppInstallStatus;
}

export interface LoadDataResult {
  loyaltyProgram: LoyaltyProgram;
  account: LoyaltyAccount;
  rewards: Reward[];
  templateCoupons: CouponReference[];
  claimedCoupons: LoyaltyCoupon[];
  referralCouponRewards: ReferralReward[];
  couponEntityNames: Record<string, string>;
  tiersProgram: TiersProgram;
}

const WARMUP_DATA_KEY = 'loadData';

export const loadData = async ({ flowAPI, appInstallStatus }: LoadDataParams): Promise<LoadDataResult> => {
  const { httpClient } = flowAPI;
  const { wixCodeApi } = flowAPI.controllerConfig;
  const referralsEnabled = appInstallStatus.isReferralsAppInstalled;
  const loyaltyEnabled = appInstallStatus.isLoyaltyAppInstalled;
  const { isSSR, isViewer } = flowAPI.environment;
  const useWarmupData = isViewer;
  const { warmupData } = wixCodeApi.window;

  // Reuse backend API responses from SSR on the client-side
  if (useWarmupData && !isSSR) {
    const dataFromSSR = warmupData.get(WARMUP_DATA_KEY);
    if (dataFromSSR) {
      return dataFromSSR;
    }
  }

  const getLoyaltyProgramPromise = loyaltyEnabled ? httpClient.request(getLoyaltyProgram({})) : undefined;
  const getTiersProgramPromise = loyaltyEnabled ? httpClient.request(getTiersProgram({})) : undefined;
  const getCurrentMemberCouponsPromise = loyaltyEnabled ? httpClient.request(getCurrentMemberCoupons({})) : undefined;

  const getReferralCouponRewardsPromise = referralsEnabled
    ? httpClient
        .request(
          queryReferralRewards({
            contactId: 'me',
            query: {
              filter: {
                rewardType: 'CouponReward',
              },
            },
          }),
        )
        // This method fails when user is not a referred friend or referring customer
        .catch(() => {})
    : undefined;

  const getCurrentMemberAccountPromise = loyaltyEnabled
    ? httpClient.request(getCurrentMemberAccount({})).catch((e) => {
        if (e instanceof Error && e.message.includes('404')) {
          return;
        }

        throw e;
      })
    : undefined;

  const rewardsAndCouponsPromise = loyaltyEnabled
    ? httpClient.request(listRewards({})).then(async ({ data }) => {
        const rewards = data.rewards ?? [];
        let templateCoupons: CouponReference[] = [];
        let templateCouponsError;

        if (rewards.length) {
          try {
            templateCoupons = await getTemplateCoupons(httpClient, rewards);
          } catch (e) {
            templateCouponsError = e;
          }
        }

        return { rewards, templateCoupons, templateCouponsError };
      })
    : {
        rewards: [],
        templateCoupons: [],
        templateCouponsError: undefined,
      };

  const [
    loyaltyProgramResponse,
    tiersProgramResponse,
    currentMemberCouponsResponse,
    currentMemberAccountResponse,
    referralCouponRewardsResponse,
    { rewards, templateCoupons, templateCouponsError },
  ] = await Promise.all([
    getLoyaltyProgramPromise,
    getTiersProgramPromise,
    getCurrentMemberCouponsPromise,
    getCurrentMemberAccountPromise,
    getReferralCouponRewardsPromise,
    rewardsAndCouponsPromise,
  ]);

  // When user has no points loyalty account doesn't exist. In that case we ignore template coupons error.
  if (currentMemberAccountResponse && templateCouponsError) {
    throw templateCouponsError;
  }

  const loyaltyProgram = loyaltyProgramResponse?.data.loyaltyProgram ?? {};
  const account = currentMemberAccountResponse?.data.account ?? {
    points: {
      balance: 0,
      redeemed: 0,
      earned: 0,
      adjusted: 0,
    },
    tier: {
      points: 0,
    },
  };

  const claimedCoupons =
    currentMemberCouponsResponse?.data?.loyaltyCoupons?.filter((coupon) => !coupon.couponReference?.deleted) ?? [];

  const referralCouponRewards =
    referralCouponRewardsResponse?.data.referralRewards?.filter(({ coupon }) => !!coupon) ?? [];

  const couponEntityNamePromises = referralCouponRewards.map(({ coupon }) => {
    const couponScope = coupon?.couponSpecification?.scope;
    return couponScope ? getCouponEntityName({ httpClient, couponScope }) : Promise.resolve(null);
  });

  const couponEntityNames: Record<string, string> = {};

  try {
    const couponEntityNameResult = await Promise.all(couponEntityNamePromises);
    couponEntityNameResult.forEach((entityName, index) => {
      if (entityName) {
        const couponId = referralCouponRewards[index].coupon!.id!;
        couponEntityNames[couponId] = entityName;
      }
    });
  } catch {}

  if (loyaltyEnabled && loyaltyProgram.status !== ProgramStatus.ACTIVE) {
    rewards.length = 0;
  }

  const tiersProgram: TiersProgram = {
    programSettings: tiersProgramResponse?.data.programSettings ?? undefined,
    tiers: tiersProgramResponse?.data.tiers ?? undefined,
  };

  const data = {
    loyaltyProgram,
    account,
    rewards,
    templateCoupons,
    claimedCoupons,
    referralCouponRewards,
    couponEntityNames,
    tiersProgram,
  };

  // Forward backend API responses from SSR to client-side
  if (useWarmupData && isSSR) {
    warmupData.set(WARMUP_DATA_KEY, data);
  }

  return data;
};

async function getTemplateCoupons(httpClient: IHttpClient, rewards: Reward[]) {
  let templateCoupons: CouponReference[] = [];
  const templateIds = rewards
    .filter((reward) => !!reward.coupon?.templateId)
    .map((reward) => reward.coupon?.templateId);

  if (templateIds.length) {
    const { data } = await httpClient.request(
      queryCouponTemplate({
        query: {
          filter: JSON.stringify({ id: { $in: templateIds } }),
        },
      }),
    );

    if (data.couponReferences) {
      templateCoupons = data.couponReferences;
    }
  }

  return templateCoupons;
}
